// src/components/CookieBanner.jsx
import React, { useEffect, useState } from "react";
import { useCookieConsent } from "../hooks/useCookieConsent";

export default function CookieBanner() {
  const {
    consent,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    settingsOpen,
    setSettingsOpen,
  } = useCookieConsent();

  // Hide for the current session if the user clicks Close (no consent recorded)
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("cookie_consent_dismissed") === "1"
  );

  // Local prefs state (fallback to false if hook hasn't hydrated yet)
  const [prefs, setPrefs] = useState({
    functional: !!consent?.functional,
    analytics: !!consent?.analytics,
    marketing: !!consent?.marketing,
  });

  // Keep local prefs in sync if consent changes (e.g., user opened settings after accepting)
  useEffect(() => {
    setPrefs({
      functional: !!consent?.functional,
      analytics: !!consent?.analytics,
      marketing: !!consent?.marketing,
    });
  }, [consent?.functional, consent?.analytics, consent?.marketing]);

  // ESC to dismiss (per-session) without recording consent
  useEffect(() => {
    if ((consent?.responded && !settingsOpen) || dismissed) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleDismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consent?.responded, settingsOpen, dismissed]);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("cookie_consent_dismissed", "1");
  };

  const handleSave = () => {
    savePreferences(prefs);
    setSettingsOpen(false);
  };

  // Hide when: (a) user already responded and settings are closed, OR (b) user dismissed for this session
  if (((consent?.responded ?? false) && !settingsOpen) || dismissed) return null;

  return (
    <div style={styles.wrap} role="dialog" aria-modal="true" aria-label="Cookie consent">
      <div style={styles.box}>
        {/* Close (dismiss) button – does NOT imply consent */}
        <button
          type="button"
          aria-label="Dismiss cookie banner"
          onClick={handleDismiss}
          style={styles.close}
        >
          <span aria-hidden>×</span>
        </button>

        {!settingsOpen && (
          <>
            <h4 style={styles.title}>We use cookies</h4>
            <p style={styles.text}>
              We use necessary cookies to run the site. We’d also like to set optional cookies
              (functional, analytics, and marketing) to improve your experience. You can change
              your choices anytime.
            </p>

            <div style={styles.actions}>
              <button type="button" style={styles.btnReject} onClick={rejectNonEssential}>
                Reject non-essential
              </button>
              <button type="button" style={styles.btnAccept} onClick={acceptAll}>
                Accept all
              </button>
              <button
                type="button"
                style={styles.btnLink}
                onClick={() => setSettingsOpen(true)}
              >
                Manage settings
              </button>
              <button type="button" style={styles.btnNeutral} onClick={handleDismiss}>
                Close
              </button>
            </div>
          </>
        )}

        {settingsOpen && (
          <>
            <h4 style={styles.title}>Cookie preferences</h4>
            <ul style={styles.list}>
              <li>
                <label style={styles.choice}>
                  <input type="checkbox" checked readOnly />{" "}
                  <span>Necessary (always on)</span>
                </label>
              </li>
              <li>
                <label style={styles.choice}>
                  <input
                    type="checkbox"
                    checked={prefs.functional}
                    onChange={(e) =>
                      setPrefs((p) => ({ ...p, functional: e.target.checked }))
                    }
                  />{" "}
                  <span>Functional (chat, UX)</span>
                </label>
              </li>
              <li>
                <label style={styles.choice}>
                  <input
                    type="checkbox"
                    checked={prefs.analytics}
                    onChange={(e) =>
                      setPrefs((p) => ({ ...p, analytics: e.target.checked }))
                    }
                  />{" "}
                  <span>Analytics</span>
                </label>
              </li>
              <li>
                <label style={styles.choice}>
                  <input
                    type="checkbox"
                    checked={prefs.marketing}
                    onChange={(e) =>
                      setPrefs((p) => ({ ...p, marketing: e.target.checked }))
                    }
                  />{" "}
                  <span>Marketing</span>
                </label>
              </li>
            </ul>

            <div style={styles.actions}>
              <button
                type="button"
                style={styles.btnGhost}
                onClick={() => setSettingsOpen(false)}
              >
                Back
              </button>
              <button type="button" style={styles.btnAccept} onClick={handleSave}>
                Save preferences
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    zIndex: 9999,
  },
  box: {
    width: "min(760px, 95%)",
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 10px 30px rgba(0,0,0,.15)",
    padding: 20,
    margin: 16,
    fontFamily:
      "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    position: "relative",
  },
  close: {
    position: "absolute",
    top: 12,
    right: 12,
    width: 40,
    height: 40,
    borderRadius: 8,
    border: "1px solid #D0D5DD",
    background: "#fff",
    cursor: "pointer",
    fontSize: 20,
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: { margin: "0 0 8px", fontSize: 18, fontWeight: 600, color: "#101828" },
  text: { margin: "0 0 16px", lineHeight: 1.55, color: "#344054" },
  actions: { display: "flex", gap: 12, justifyContent: "flex-end", flexWrap: "wrap" },

  // Primary
  btnAccept: {
    background: "#276F8B",
    color: "#fff",
    border: 0,
    padding: "10px 14px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
  },
  // Solid "Reject" (not transparent)
  btnReject: {
    background: "#fff",
    color: "#344054",
    border: "1px solid #D0D5DD",
    padding: "10px 14px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
  },
  // Neutral Close
  btnNeutral: {
    background: "#fff",
    color: "#344054",
    border: "1px dashed #D0D5DD",
    padding: "10px 14px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 500,
  },
  // Ghost (used on Back)
  btnGhost: {
    background: "transparent",
    color: "#344054",
    border: "1px solid #D0D5DD",
    padding: "10px 14px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 500,
  },
  btnLink: {
    background: "transparent",
    border: 0,
    color: "#276F8B",
    cursor: "pointer",
    textDecoration: "underline",
    fontWeight: 600,
  },
  list: {
    listStyle: "none",
    padding: 0,
    margin: "8px 0 16px",
    display: "grid",
    gap: 10,
  },
  choice: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: "#344054",
  },
};
